import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import formatByteSize from '@UI/Helpers/Outbox/formatByteSize';
import ImportDropZone from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/RecordInterviewSheet/ImportAudio/ImportDropZone';
import useInterviewImport from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/RecordInterviewSheet/ImportAudio/Hooks/useInterviewImport';
import type { ImportRejectionReason } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/RecordInterviewSheet/ImportAudio/Hooks/useInterviewImport';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import * as React from 'react';
import { Message, ProgressBar, Skeleton } from '@verclam/ui-kit';

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

/** French sentence for a file-validation rejection — every limit it quotes comes straight from the
 * policy, never from a front-coded value. */
const describeRejection = (
	reason: ImportRejectionReason,
	acceptedFormatsSentence: string,
	maxDurationSentence: string,
	maxByteSize: number,
): string => {
	switch (reason) {
		case 'unsupported-format':
			return `Ce format n'est pas pris en charge. Formats acceptés : ${acceptedFormatsSentence}.`;
		case 'unreadable':
			return "Ce fichier n'a pas pu être lu, ou ne contient aucune piste audio.";
		case 'too-large':
			return `Ce fichier dépasse la limite autorisée (${maxDurationSentence} maximum, ${formatByteSize(maxByteSize)} maximum).`;
	}
};

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly onImported: () => void;
};

/**
 * The import of an audio file, inside the « Enregistrer un entretien » sheet: the drop zone, every
 * rejection, and the progress of the local slicing. Loading and failing the recording policy are
 * rendered here, since the limits it quotes are the policy's own.
 */
const ImportAudioPanel: React.FC<Props> = ({ subject, preparationId, onImported }) => {
	const { currentFacility } = useStrictCurrentFacility();
	const { user } = useAuthenticatedUser();
	const ownerUserId = user !== null ? user.id : null;

	const {
		policy,
		policyError,
		refetchPolicy,
		rejection,
		preparationError,
		preparingProgress,
		handleFileSelected,
	} = useInterviewImport({
		facilityId: currentFacility.id,
		subject,
		preparationId,
		ownerUserId,
		onImported,
	});

	if (policy === null && policyError === null) {
		return <Skeleton height="10rem" animation="wave" shape="rectangle" />;
	}

	if (policy === null) {
		return (
			<ErrorState title="Les paramètres d'import n'ont pas pu être chargés." onRetry={refetchPolicy} />
		);
	}

	return (
		<div css={panelStyle}>
			<ConditionalLoader data={preparationError}>
				{(message) => <Message severity="error" text={message} />}
			</ConditionalLoader>
			<ConditionalLoader data={preparingProgress === null ? rejection : null}>
				{(reason) => (
					<Message
						severity="warn"
						text={describeRejection(
							reason,
							policy.acceptedFormatsSentence,
							policy.maxDurationSentence,
							policy.maxSegments * policy.segmentMaxBytes,
						)}
					/>
				)}
			</ConditionalLoader>
			{preparingProgress !== null ? (
				<React.Fragment>
					<ProgressBar
						value={preparingProgress * 100}
						aria-label="Avancement de la préparation du fichier"
					/>
					<p css={noteStyle}>
						Préparation du fichier en cours : elle ne peut pas être annulée une fois commencée.
					</p>
				</React.Fragment>
			) : (
				<ImportDropZone
					acceptedExtensions={policy.acceptedExtensions}
					acceptedFormatsSentence={policy.acceptedFormatsSentence}
					maxDurationSentence={policy.maxDurationSentence}
					onFileSelected={handleFileSelected}
				/>
			)}
		</div>
	);
};

export default ImportAudioPanel;
